import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useHardwareBackHandler } from "../../../navigation/HardwareBackContext";

import { waitForStepCompletion } from "./completionEventRegistry";

import { TourSpotlight } from "./TourSpotlight";

import { TourTooltip } from "./TourTooltip";

import { useSpotlightRect, useSpotlightRects } from "./useSpotlightRect";

import { waitForElement } from "./waitForElement";

import "./tourEngine.css";



export const GUIDED_MODE_SCOPES = Object.freeze({

  SUSPEND_AUTO_DISMISS: "suspend-auto-dismiss",

});



const DEFAULT_SECONDARY_TIMEOUT_MS = 12000;



function isInformationInteraction(interaction) {

  if (!interaction) {

    return true;

  }

  return interaction.type === "information" || interaction.mode === "next";

}



function buildPresentation(step, stepPhase) {

  if (stepPhase === "secondary" && step.targetTransition) {

    const transition = step.targetTransition;

    return {

      title: transition.title || step.title,

      description: transition.description || step.description,

      placement: transition.placement || step.placement || "auto",

    };

  }



  return {

    title: step.title,

    description: step.description,

    placement: step.placement || "auto",

  };

}



/**

 * Generic guided-tour engine — executes a supplied step sequence with no domain knowledge.

 */

export function TourEngine({

  steps,

  initialStepIndex = 0,

  allowSkip = false,

  allowPrevious = true,

  allowHardwareBackDismiss = false,

  allowDismissOnOffTargetInteraction = false,

  estimatedDurationMinutes = 1,

  onComplete,

  onCancel,

  onGuidedModeChange,

}) {

  const [stepIndex, setStepIndex] = useState(initialStepIndex);

  const [stepPhase, setStepPhase] = useState("primary");

  const [targetElement, setTargetElement] = useState(null);

  const [secondaryTargetElements, setSecondaryTargetElements] = useState([]);

  const [targetMissing, setTargetMissing] = useState(false);

  const [waitingForInteraction, setWaitingForInteraction] = useState(false);

  const [canGoNext, setCanGoNext] = useState(false);



  const interactionAbortRef = useRef(null);

  const mountedRef = useRef(true);



  const currentStep = steps[stepIndex] || null;

  const totalSteps = steps.length;



  const activeInteraction = useMemo(() => {

    if (!currentStep || currentStep.kind === "completion") {

      return null;

    }

    if (stepPhase === "secondary" && currentStep.targetTransition?.interaction) {

      return currentStep.targetTransition.interaction;

    }

    return currentStep.interaction || null;

  }, [currentStep, stepPhase]);



  const isInformationStep = useMemo(

    () => isInformationInteraction(activeInteraction),

    [activeInteraction],

  );



  const presentation = useMemo(

    () => (currentStep ? buildPresentation(currentStep, stepPhase) : null),

    [currentStep, stepPhase],

  );



  const rect = useSpotlightRect(targetElement, Boolean(currentStep && currentStep.kind === "step"));

  const showSecondarySpotlights =
    Boolean(currentStep?.kind === "step" && stepPhase === "primary");

  const secondaryRects = useSpotlightRects(
    secondaryTargetElements,
    showSecondarySpotlights,
  );



  useEffect(() => {

    onGuidedModeChange?.({

      isActive: true,

      stepId: currentStep?.id ?? null,

      stepPhase: currentStep?.kind === "step" ? stepPhase : null,

      scopes: currentStep?.guidedModeScopes ?? [],

    });

  }, [currentStep, stepPhase, onGuidedModeChange]);



  useEffect(

    () => () => {

      onGuidedModeChange?.({

        isActive: false,

        stepId: null,

        stepPhase: null,

        scopes: [],

      });

    },

    [onGuidedModeChange],

  );



  useHardwareBackHandler(() => {

    if (allowHardwareBackDismiss || allowDismissOnOffTargetInteraction) {

      onCancel?.();

      return true;

    }

    return true;

  }, Boolean(currentStep));



  useEffect(() => {

    mountedRef.current = true;

    return () => {

      mountedRef.current = false;

    };

  }, []);



  const clearInteractionWait = useCallback(() => {

    interactionAbortRef.current?.abort();

    interactionAbortRef.current = null;

    setWaitingForInteraction(false);

  }, []);



  const resolveTargetBySelector = useCallback(async (selector, options = {}) => {

    const { scrollIntoView = false, timeoutMs } = options;



    if (!selector) {

      setTargetElement(null);

      setTargetMissing(false);

      return null;

    }



    const element = await waitForElement(selector, { timeoutMs });



    if (!mountedRef.current) {

      return null;

    }



    if (!element) {

      setTargetElement(null);

      setTargetMissing(true);

      return null;

    }



    if (scrollIntoView) {

      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

    }



    setTargetElement(element);

    setTargetMissing(false);

    return element;

  }, []);



  const resolveSecondaryTargets = useCallback(async (selectors, timeoutMs) => {

    if (!selectors?.length) {

      setSecondaryTargetElements([]);

      return;

    }



    const elements = await Promise.all(

      selectors.map((selector) =>

        waitForElement(selector, {

          timeoutMs: timeoutMs ?? DEFAULT_SECONDARY_TIMEOUT_MS,

        }),

      ),

    );



    if (!mountedRef.current) {

      return;

    }



    setSecondaryTargetElements(elements.filter(Boolean));

  }, []);



  const waitForInteraction = useCallback(

    async (interaction, element) => {

      if (!interaction || isInformationInteraction(interaction)) {

        setCanGoNext(true);

        setWaitingForInteraction(false);

        return true;

      }



      clearInteractionWait();

      setCanGoNext(false);

      setWaitingForInteraction(true);



      const abortController = new AbortController();

      interactionAbortRef.current = abortController;



      await waitForStepCompletion(interaction, {

        target: element,

        signal: abortController.signal,

      });



      if (!mountedRef.current || abortController.signal.aborted) {

        return false;

      }



      setCanGoNext(true);

      setWaitingForInteraction(false);

      return true;

    },

    [clearInteractionWait],

  );



  const advanceToNextStep = useCallback(() => {

    setStepPhase("primary");

    setStepIndex((current) => {

      if (current >= totalSteps - 1) {

        return current;

      }

      return current + 1;

    });

  }, [totalSteps]);



  useEffect(() => {

    if (!currentStep) {

      return undefined;

    }



    let cancelled = false;



    const bootStep = async () => {

      clearInteractionWait();



      if (currentStep.kind === "completion") {

        setTargetElement(null);

        setSecondaryTargetElements([]);

        setTargetMissing(false);

        setCanGoNext(true);

        return;

      }



      if (stepPhase === "primary") {

        setCanGoNext(isInformationStep && !currentStep.targetTransition);



        const element = await resolveTargetBySelector(currentStep.targetSelector, {

          scrollIntoView: currentStep.scrollIntoView,

        });

        if (cancelled) {

          return;

        }



        if (currentStep.secondaryTargetSelectors?.length) {

          resolveSecondaryTargets(

            currentStep.secondaryTargetSelectors,

            currentStep.secondaryTargetTimeoutMs,

          );

        } else {

          setSecondaryTargetElements([]);

        }



        if (!isInformationStep) {

          const completed = await waitForInteraction(currentStep.interaction, element);

          if (cancelled || !completed) {

            return;

          }



          if (currentStep.targetTransition) {

            setStepPhase("secondary");

            return;

          }



          advanceToNextStep();

        } else if (isInformationStep) {

          setCanGoNext(true);

        }



        return;

      }



      if (stepPhase === "secondary" && currentStep.targetTransition) {

        setSecondaryTargetElements([]);

        const transition = currentStep.targetTransition;

        const secondaryInteraction = transition.interaction || {

          type: "information",

          mode: "next",

        };

        const secondaryIsInformation = isInformationInteraction(secondaryInteraction);



        setCanGoNext(secondaryIsInformation);



        const secondaryElement = await resolveTargetBySelector(transition.selector, {

          scrollIntoView: transition.scrollIntoView,

          timeoutMs: transition.timeoutMs ?? DEFAULT_SECONDARY_TIMEOUT_MS,

        });

        if (cancelled) {

          return;

        }



        if (!secondaryIsInformation) {

          const completed = await waitForInteraction(secondaryInteraction, secondaryElement);

          if (cancelled || !completed) {

            return;

          }

          advanceToNextStep();

        } else {

          setCanGoNext(true);

        }

      }

    };



    bootStep();



    return () => {

      cancelled = true;

      clearInteractionWait();

    };

  }, [

    currentStep,

    stepPhase,

    isInformationStep,

    resolveTargetBySelector,

    waitForInteraction,

    clearInteractionWait,

    resolveSecondaryTargets,

    advanceToNextStep,

  ]);



  const goToStep = useCallback(

    (nextIndex) => {

      if (nextIndex < 0 || nextIndex >= totalSteps) {

        return;

      }

      clearInteractionWait();

      setStepPhase("primary");

      setStepIndex(nextIndex);

    },

    [clearInteractionWait, totalSteps],

  );



  const handleNext = useCallback(() => {

    if (!canGoNext && !isInformationStep) {

      return;

    }



    if (stepPhase === "secondary" || isInformationStep) {

      advanceToNextStep();

      return;

    }



    goToStep(stepIndex + 1);

  }, [canGoNext, isInformationStep, stepPhase, advanceToNextStep, goToStep, stepIndex]);



  const handlePrevious = useCallback(() => {

    if (stepPhase === "secondary") {

      setStepPhase("primary");

      return;

    }

    goToStep(stepIndex - 1);

  }, [goToStep, stepIndex, stepPhase]);



  const handleFinish = useCallback(() => {

    clearInteractionWait();

    onComplete?.();

  }, [clearInteractionWait, onComplete]);



  const handleSkip = useCallback(() => {

    clearInteractionWait();

    onCancel?.();

  }, [clearInteractionWait, onCancel]);



  useEffect(() => {

    if (!allowDismissOnOffTargetInteraction || !currentStep) {

      return undefined;

    }



    const isAllowedTarget = (eventTarget) => {

      if (!(eventTarget instanceof Element)) {

        return false;

      }

      if (targetElement?.contains(eventTarget)) {

        return true;

      }

      if (secondaryTargetElements.some((element) => element?.contains(eventTarget))) {

        return true;

      }

      if (eventTarget.closest(".tour-engine__tooltip")) {

        return true;

      }

      return false;

    };



    const handlePointerDown = (event) => {

      if (isAllowedTarget(event.target)) {

        return;

      }

      handleSkip();

    };



    document.addEventListener("pointerdown", handlePointerDown, true);



    return () => {

      document.removeEventListener("pointerdown", handlePointerDown, true);

    };

  }, [

    allowDismissOnOffTargetInteraction,

    currentStep,

    targetElement,

    secondaryTargetElements,

    handleSkip,

  ]);



  if (!currentStep || !presentation) {

    return null;

  }



  const showTooltip = currentStep.kind === "completion" || !currentStep.hideTooltip;



  return (

    <div className="tour-engine" role="presentation">

      {currentStep.kind === "step" ? (
        <TourSpotlight primaryRect={rect} secondaryRects={secondaryRects} />
      ) : (

        <div className="tour-engine__backdrop tour-engine__backdrop--full" aria-hidden="true" />

      )}



      {showTooltip ? (
      <TourTooltip

        presentation={presentation}

        step={currentStep}

        stepIndex={stepIndex}

        totalSteps={totalSteps}

        estimatedDurationMinutes={estimatedDurationMinutes}

        rect={rect}

        allowSkip={allowSkip}

        allowPrevious={allowPrevious}

        canGoNext={canGoNext}

        waitingForInteraction={waitingForInteraction}

        isInformationStep={isInformationStep}

        onNext={handleNext}

        onPrevious={handlePrevious}

        onFinish={handleFinish}

        onSkip={handleSkip}

      />
      ) : null}



      {targetMissing ? (

        <p className="tour-engine__target-missing" role="status">

          Highlight unavailable — use Next when you are ready to continue.

        </p>

      ) : null}

    </div>

  );

}

